import React, { createContext, useContext, useState, useEffect } from "react";
import { shouldBypassAuth, makeFakeJWT, DEV_USER } from "../services/utils/devAuthBypass";
import { validateAuthToken } from "../services/ApiService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiry = payload.exp * 1000;
    return Date.now() > expiry;
  } catch (error) {
    console.error("[AuthContext] Invalid token format:", error);
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true); // Add a loading state

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      const savedToken = localStorage.getItem("authToken");
      const savedUserRaw = localStorage.getItem("userDetails");

      if (!savedToken || isTokenExpired(savedToken)) {
        logout();
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      try {
        await validateAuthToken(savedToken);

        if (cancelled) {
          return;
        }

        setToken(savedToken);

        if (savedUserRaw) {
          try {
            setUser(JSON.parse(savedUserRaw));
          } catch (error) {
            console.error("[AuthContext] Failed to parse stored user details:", error);
            localStorage.removeItem("userDetails");
          }
        }
      } catch (error) {
        console.error("[AuthContext] Token validation failed:", error);
        logout();

        if (error?.status === 401 && typeof window !== "undefined") {
          window.location.replace("/");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = (userDetails, authToken) => {
    //console.log("[AuthContext] Logging in user with token:", authToken);
    console.log("[AuthContext] Logging in user with user:", userDetails);
    setUser(userDetails);
    setToken(authToken);
    localStorage.setItem("authToken", authToken);
    localStorage.setItem("userDetails", JSON.stringify(userDetails));
  };

  const logout = () => {
    console.warn("[AuthContext] Logout triggered unexpectedly.");
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
  };


  if (loading) {
    return <div>Loading...</div>; // Show a loading state while checking session
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, setUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};